import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Plus, Search, Download, Upload, Phone, MessageCircle, Mail, X, Columns3, LayoutGrid, Table as TableIcon, Bookmark, BookmarkPlus, Trash2, Pencil, ArrowUp, ArrowDown, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import LeadsImportDialog from "./LeadsImportDialog";

const fmt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return d; } };
const stageColor = (s = "") => ({
  "New Lead": "bg-slate-100 text-slate-700 border-slate-300",
  "Call Attempt 1": "bg-blue-50 text-blue-800 border-blue-200",
  "Call Attempt 2": "bg-blue-50 text-blue-800 border-blue-200",
  "Call Attempt 3": "bg-blue-50 text-blue-800 border-blue-200",
  "Fourth Message Sent": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Final Call Attempt": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Follow-Up Scheduled": "bg-amber-50 text-amber-800 border-amber-200",
  "Retained": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Lost": "bg-rose-50 text-rose-800 border-rose-200",
  "Dead / Unresponsive": "bg-slate-100 text-slate-500 border-slate-200",
}[s] || "bg-slate-50 text-slate-700 border-slate-200");

const ALL_COLS = [
  { key: "checkbox", label: "", always: true, w: "w-8" },
  { key: "lead_id", label: "Lead ID" },
  { key: "full_name", label: "Name", always: true },
  { key: "phone", label: "Phone" },
  { key: "department", label: "Team", editable: true },
  { key: "source", label: "Source", editable: true },
  { key: "app_type", label: "File Type" },
  { key: "stage", label: "Stage", editable: true },
  { key: "caller", label: "Caller", editable: true },
  { key: "last_contact", label: "Last Contact" },
  { key: "next_followup", label: "Next Follow-Up", editable: true },
  { key: "notes", label: "Notes", editable: true },
  { key: "age", label: "Age" },
  { key: "actions", label: "", always: true, w: "w-32" },
];

const DEFAULT_VISIBLE = ALL_COLS.map(c => c.key);

function SortableTh({ label, col, sort, onClick, align = "left" }) {
  const active = sort.col === col;
  return (
    <th className={`text-${align} px-3 py-2 cursor-pointer select-none hover:text-slate-800`} onClick={() => onClick(col)} data-testid={`sort-${col}`}>
      <span className="inline-flex items-center gap-1">{label}
        {active && (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}

export default function LeadsList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ app_types: [], callers: [], stages: [], sources: [], allowed_departments: [] });
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL");
  const [caller, setCaller] = useState("ALL");
  const [stage, setStage] = useState("ALL");
  const [source, setSource] = useState("ALL");
  const [bucket, setBucket] = useState("ALL");
  const [dept, setDept] = useState("ALL");
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("leadsViewMode") || "grid");
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem("leadsCols");
    return saved ? JSON.parse(saved) : DEFAULT_VISIBLE;
  });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(() => localStorage.getItem("leadsActiveView") || "");
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [renameView, setRenameView] = useState(null); // {id, name}
  const [sort, setSort] = useState(() => {
    const saved = localStorage.getItem("leadsSort");
    return saved ? JSON.parse(saved) : { col: null, dir: "asc" };
  });
  const today = new Date().toISOString().slice(0, 10);
  const canBulkAssign = ["admin", "manager", "management"].includes(user?.role);
  const canImport = ["admin", "manager", "management", "smm"].includes(user?.role);
  const canShareViews = ["admin", "manager", "management", "filing_manager"].includes(user?.role);
  const shareScopes = ["admin", "manager", "management"].includes(user?.role) ? ["STUDY", "FILING", "ALL"]
                    : user?.role === "filing_manager" ? ["FILING"] : [];

  useEffect(() => { localStorage.setItem("leadsViewMode", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("leadsCols", JSON.stringify(visibleCols)); }, [visibleCols]);
  useEffect(() => { localStorage.setItem("leadsActiveView", activeViewId || ""); }, [activeViewId]);
  useEffect(() => { localStorage.setItem("leadsSort", JSON.stringify(sort)); }, [sort]);

  // Sort rows client-side
  const sortedRows = useMemo(() => {
    if (!sort.col) return rows;
    const accessor = {
      lead_id: l => l.lead_id,
      full_name: l => (l.full_name || "").toLowerCase(),
      phone: l => l.phone || "",
      department: l => l.department || "",
      source: l => l.source || "",
      app_type: l => (l.app_type_name || "").toLowerCase(),
      stage: l => l.stage || "",
      caller: l => (l.caller_name || "").toLowerCase(),
      last_contact: l => l.last_contacted_at || "",
      next_followup: l => l.next_followup_at || "",
      notes: l => (l.notes || "").toLowerCase(),
      age: l => l.age_days ?? -1,
    }[sort.col];
    if (!accessor) return rows;
    const sorted = [...rows].sort((a, b) => {
      const va = accessor(a), vb = accessor(b);
      if (va === vb) return 0;
      if (va === "" || va == null) return 1;
      if (vb === "" || vb == null) return -1;
      return va < vb ? -1 : 1;
    });
    return sort.dir === "desc" ? sorted.reverse() : sorted;
  }, [rows, sort]);

  const toggleSort = (col) => setSort(s => {
    if (s.col !== col) return { col, dir: "asc" };
    if (s.dir === "asc") return { col, dir: "desc" };
    return { col: null, dir: "asc" };
  });

  const loadViews = useCallback(async () => {
    try { const r = await api.get("/leads/views"); setSavedViews(r.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const currentFilters = () => ({ q, type, caller, stage, source, bucket, dept });

  const applyView = (v) => {
    const f = v.filters || {};
    setQ(f.q || ""); setType(f.type || "ALL"); setCaller(f.caller || "ALL");
    setStage(f.stage || "ALL"); setSource(f.source || "ALL");
    setBucket(f.bucket || "ALL"); setDept(f.dept || "ALL");
    if (v.visible_cols?.length) setVisibleCols(v.visible_cols);
    if (v.view_mode) setViewMode(v.view_mode);
    setSort(v.sort || { col: null, dir: "asc" });
    setActiveViewId(v.id);
    toast.success(`View "${v.name}" applied`);
  };

  const saveNewView = async (name, shared) => {
    try {
      const r = await api.post("/leads/views", { name, filters: currentFilters(), visible_cols: visibleCols, view_mode: viewMode, sort, shared: shared || null });
      setSavedViews(vs => [...vs, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setActiveViewId(r.data.id);
      toast.success(`Saved as "${name}"${shared ? ` · shared with ${shared === "ALL" ? "everyone" : shared}` : ""}`);
      setSaveViewOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const overwriteView = async () => {
    const v = savedViews.find(x => x.id === activeViewId);
    if (!v || !v.is_owner) return;
    try {
      await api.patch(`/leads/views/${v.id}`, { filters: currentFilters(), visible_cols: visibleCols, view_mode: viewMode, sort });
      toast.success(`View "${v.name}" updated`);
      loadViews();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const toggleShareView = async (v, scope) => {
    try {
      await api.patch(`/leads/views/${v.id}`, { shared: scope || null });
      toast.success(scope ? `Shared with ${scope === "ALL" ? "everyone" : scope}` : "Made private");
      loadViews();
    } catch (e) { toast.error(e.response?.data?.detail || "Share failed"); }
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?`)) return;
    try {
      await api.delete(`/leads/views/${v.id}`);
      setSavedViews(vs => vs.filter(x => x.id !== v.id));
      if (activeViewId === v.id) setActiveViewId("");
      toast.success("View deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const renameSubmit = async () => {
    if (!renameView) return;
    try {
      await api.patch(`/leads/views/${renameView.id}`, { name: renameView.name });
      setSavedViews(vs => vs.map(v => v.id === renameView.id ? { ...v, name: renameView.name } : v).sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Renamed");
      setRenameView(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };

  const clearActiveView = () => { setActiveViewId(""); toast.message("Cleared active view (filters kept)"); };
  const activeView = savedViews.find(v => v.id === activeViewId);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (type !== "ALL") params.set("app_type_id", type);
    if (caller !== "ALL") params.set("assigned_caller_id", caller);
    if (stage !== "ALL") params.set("stage", stage);
    if (source !== "ALL") params.set("source", source);
    if (bucket !== "ALL") params.set("bucket", bucket);
    if (dept !== "ALL") params.set("department", dept);
    const [r, m] = await Promise.all([api.get(`/leads?${params.toString()}`), api.get("/leads/meta")]);
    setRows(r.data); setMeta(m.data);
  }, [q, type, caller, stage, source, bucket, dept]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const clearFilters = () => { setQ(""); setType("ALL"); setCaller("ALL"); setStage("ALL"); setSource("ALL"); setBucket("ALL"); setDept("ALL"); };
  const download = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (bucket !== "ALL") params.set("bucket", bucket);
    if (stage !== "ALL") params.set("stage", stage);
    if (source !== "ALL") params.set("source", source);
    if (type !== "ALL") params.set("app_type_id", type);
    if (caller !== "ALL") params.set("assigned_caller_id", caller);
    if (dept !== "ALL") params.set("department", dept);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/leads/export.csv?${params.toString()}`, "_blank");
  };

  // Inline PATCH — call after any cell change
  const patchLead = async (id, body) => {
    // Optimistic
    setRows(r => r.map(x => x.id === id ? { ...x, ...body } : x));
    try { await api.patch(`/leads/${id}`, body); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); load(); }
  };

  // Selection
  const toggleOne = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  const clearSel = () => setSelected(new Set());

  // Bulk actions
  const bulk = async (action, value) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await api.post("/leads/bulk", { action, value, lead_ids: ids });
      toast.success(`${r.data.updated} lead${r.data.updated === 1 ? "" : "s"} updated${r.data.skipped_permission ? ` · ${r.data.skipped_permission} skipped (no permission)` : ""}`);
      clearSel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk update failed"); }
  };

  const showCol = (k) => visibleCols.includes(k) || ALL_COLS.find(c => c.key === k)?.always;
  const gridMode = viewMode === "grid";
  const isCompact = viewMode === "compact";

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} shown{selected.size > 0 ? ` · ${selected.size} selected` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Saved Views */}
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="leads-views" variant="outline">
                <Bookmark className="w-4 h-4 mr-1" />
                {activeView ? <span className="max-w-[140px] truncate">{activeView.name}</span> : "Views"}
                {savedViews.length > 0 && !activeView && <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{savedViews.length}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-white p-2" align="end">
              <div className="text-xs text-slate-500 px-2 py-1 uppercase tracking-widest">My Views</div>
              {savedViews.filter(v => v.is_owner).length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No saved views yet. Set filters/columns and click "Save current view".</div>}
              {savedViews.filter(v => v.is_owner).map(v => (
                <div key={v.id} className={`flex items-center gap-1 px-1 py-1 rounded hover:bg-slate-50 group ${activeViewId === v.id ? "bg-orange-50" : ""}`}>
                  <button onClick={() => applyView(v)} data-testid={`view-apply-${v.name}`} className="flex-1 text-left text-sm px-2 py-1 truncate flex items-center gap-1">
                    {activeViewId === v.id && <span className="text-orange-500">●</span>}
                    <span className="truncate">{v.name}</span>
                    {v.shared && <span title={`Shared with ${v.shared === "ALL" ? "everyone" : v.shared}`} className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase font-semibold"><Users className="w-2.5 h-2.5 inline mr-0.5" />{v.shared === "ALL" ? "All" : v.shared}</span>}
                  </button>
                  {canShareViews && (
                    <select value={v.shared || ""} onChange={e => toggleShareView(v, e.target.value || null)} onClick={e => e.stopPropagation()} data-testid={`view-share-${v.name}`} className="opacity-0 group-hover:opacity-100 text-[10px] border border-slate-200 rounded px-1 py-0.5">
                      <option value="">Private</option>
                      {shareScopes.map(s => <option key={s} value={s}>{s === "ALL" ? "Everyone" : s}</option>)}
                    </select>
                  )}
                  <button onClick={() => setRenameView({ id: v.id, name: v.name })} title="Rename" className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-slate-900"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => deleteView(v)} title="Delete" data-testid={`view-delete-${v.name}`} className="opacity-0 group-hover:opacity-100 p-1 text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {savedViews.filter(v => !v.is_owner).length > 0 && (<>
                <div className="text-xs text-slate-500 px-2 py-1 uppercase tracking-widest mt-2 flex items-center gap-1"><Users className="w-3 h-3" />Team Views</div>
                {savedViews.filter(v => !v.is_owner).map(v => (
                  <div key={v.id} className={`flex items-center gap-1 px-1 py-1 rounded hover:bg-slate-50 ${activeViewId === v.id ? "bg-orange-50" : ""}`}>
                    <button onClick={() => applyView(v)} data-testid={`view-apply-${v.name}`} className="flex-1 text-left text-sm px-2 py-1 truncate flex items-center gap-1">
                      {activeViewId === v.id && <span className="text-orange-500">●</span>}
                      <span className="truncate">{v.name}</span>
                      <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase font-semibold">{v.shared === "ALL" ? "All" : v.shared}</span>
                    </button>
                    <span className="text-[10px] text-slate-400 pr-2">by {v.owner_name}</span>
                  </div>
                ))}
              </>)}
              <div className="border-t border-slate-100 mt-1 pt-1 space-y-0.5">
                <button onClick={() => setSaveViewOpen(true)} data-testid="view-save-current" className="w-full text-left text-sm px-3 py-1.5 flex items-center gap-2 hover:bg-slate-50 rounded text-slate-800">
                  <BookmarkPlus className="w-4 h-4" />Save current view…
                </button>
                {activeView && activeView.is_owner && (
                  <button onClick={overwriteView} className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 rounded text-slate-600">Update "{activeView.name}" with current filters</button>
                )}
                {activeView && (
                  <button onClick={clearActiveView} className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 rounded text-slate-500">Clear active view label</button>
                )}
              </div>
            </PopoverContent>
          </Popover>
          {/* View toggle */}
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
            <button data-testid="view-grid" onClick={() => setViewMode("grid")} className={`px-3 py-1.5 flex items-center gap-1 ${gridMode ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}><LayoutGrid className="w-4 h-4" />Grid</button>
            <button data-testid="view-compact" onClick={() => setViewMode("compact")} className={`px-3 py-1.5 flex items-center gap-1 ${isCompact ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}><TableIcon className="w-4 h-4" />Compact</button>
          </div>
          {/* Column picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="leads-columns"><Columns3 className="w-4 h-4 mr-1" />Columns</Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 bg-white p-2">
              <div className="text-xs text-slate-500 px-2 py-1">Show/hide columns</div>
              {ALL_COLS.filter(c => !c.always && c.key !== "checkbox").map(c => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50 rounded cursor-pointer">
                  <input type="checkbox" checked={visibleCols.includes(c.key)} onChange={() => setVisibleCols(v => v.includes(c.key) ? v.filter(k => k !== c.key) : [...v, c.key])} />
                  {c.label}
                </label>
              ))}
              <button onClick={() => setVisibleCols(DEFAULT_VISIBLE)} className="w-full text-left text-xs text-orange-600 hover:underline px-2 py-1 mt-1">Reset</button>
            </PopoverContent>
          </Popover>
          {canImport && <Button data-testid="leads-import" variant="outline" onClick={() => setImportOpen(true)}><Upload className="w-4 h-4 mr-1" />Import CSV</Button>}
          <Button data-testid="leads-export" variant="outline" onClick={download}><Download className="w-4 h-4 mr-1" />Export</Button>
          <Button data-testid="leads-new" onClick={() => nav("/leads/new")} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />New Lead</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="leads-search" className="pl-9" placeholder="Search name, phone, email, ID, notes…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue placeholder="File Type" /></SelectTrigger><SelectContent><SelectItem value="ALL">All types</SelectItem>{meta.app_types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={caller} onValueChange={setCaller}><SelectTrigger><SelectValue placeholder="Caller" /></SelectTrigger><SelectContent><SelectItem value="ALL">All callers</SelectItem>{meta.callers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select>
        <Select value={stage} onValueChange={setStage}><SelectTrigger><SelectValue placeholder="Stage" /></SelectTrigger><SelectContent><SelectItem value="ALL">All stages</SelectItem>{(meta.stages || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={source} onValueChange={setSource}><SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger><SelectContent><SelectItem value="ALL">All sources</SelectItem>{(meta.sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
        <span className="uppercase tracking-widest text-slate-400">Team:</span>
        {["ALL", "STUDY", "FILING"].map(d => {
          const allowed = meta.allowed_departments || ["STUDY", "FILING"];
          const disabled = d !== "ALL" && !allowed.includes(d);
          return (
            <button key={d} onClick={() => !disabled && setDept(d)} disabled={disabled} data-testid={`leads-dept-${d}`}
              className={`px-3 py-1 rounded-full border ${disabled ? "opacity-40 cursor-not-allowed" : ""} ${dept === d ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 hover:bg-slate-50"}`}>
              {d === "ALL" ? "All" : d === "STUDY" ? "Study" : "Filing"}
            </button>
          );
        })}
        <span className="uppercase tracking-widest text-slate-400 ml-2">Bucket:</span>
        {["ALL", "active", "retained", "lost", "dead"].map(b => (
          <button key={b} onClick={() => setBucket(b)} data-testid={`leads-bucket-${b}`}
            className={`px-3 py-1 rounded-full border ${bucket === b ? "bg-orange-500 text-white border-orange-500" : "border-slate-200 hover:bg-slate-50"}`}>{b}</button>
        ))}
        <Button variant="ghost" size="sm" onClick={clearFilters}><X className="w-3 h-3 mr-1" />Clear filters</Button>
      </div>

      {/* Grid table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0 z-10">
            <tr>
              {gridMode && showCol("checkbox") && (
                <th className="text-left px-3 py-2 w-8"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} data-testid="leads-select-all" /></th>
              )}
              {showCol("lead_id") && <SortableTh label="Lead ID" col="lead_id" sort={sort} onClick={toggleSort} />}
              {showCol("full_name") && <SortableTh label="Name" col="full_name" sort={sort} onClick={toggleSort} />}
              {showCol("phone") && <SortableTh label="Phone" col="phone" sort={sort} onClick={toggleSort} />}
              {showCol("department") && <SortableTh label="Team" col="department" sort={sort} onClick={toggleSort} />}
              {showCol("source") && <SortableTh label="Source" col="source" sort={sort} onClick={toggleSort} />}
              {showCol("app_type") && <SortableTh label="File Type" col="app_type" sort={sort} onClick={toggleSort} />}
              {showCol("stage") && <SortableTh label="Stage" col="stage" sort={sort} onClick={toggleSort} />}
              {showCol("caller") && <SortableTh label="Caller" col="caller" sort={sort} onClick={toggleSort} />}
              {showCol("last_contact") && <SortableTh label="Last Contact" col="last_contact" sort={sort} onClick={toggleSort} />}
              {showCol("next_followup") && <SortableTh label="Next Follow-Up" col="next_followup" sort={sort} onClick={toggleSort} />}
              {showCol("notes") && <SortableTh label="Notes" col="notes" sort={sort} onClick={toggleSort} />}
              {showCol("age") && <SortableTh label="Age" col="age" sort={sort} onClick={toggleSort} align="right" />}
              {showCol("actions") && <th className="text-right px-3 py-2 w-32">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(l => {
              const nf = l.next_followup_at ? new Date(l.next_followup_at) : null;
              const nfDate = nf ? nf.toISOString().slice(0, 10) : null;
              const rowCls = nfDate && nfDate < today && !["Retained", "Lost", "Dead / Unresponsive"].includes(l.stage) ? "bg-rose-50/50"
                : nfDate === today ? "bg-amber-50/50" : "";
              const isSel = selected.has(l.id);
              return (
                <tr key={l.id} data-testid={`lead-row-${l.lead_id}`} className={`border-t border-slate-100 hover:bg-slate-50 ${rowCls} ${isSel ? "bg-orange-50/50" : ""}`}>
                  {gridMode && showCol("checkbox") && (
                    <td className="px-3 py-2"><input type="checkbox" checked={isSel} onChange={() => toggleOne(l.id)} data-testid={`lead-select-${l.lead_id}`} /></td>
                  )}
                  {showCol("lead_id") && <td className="px-3 py-2 mono text-xs text-orange-600 cursor-pointer" onClick={() => nav(`/leads/${l.id}`)}>{l.lead_id}</td>}
                  {showCol("full_name") && (
                    <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => nav(`/leads/${l.id}`)}>
                      {l.full_name}
                      <div className="text-xs text-slate-500">{l.email || ""}</div>
                      {l.sla_breached && <span data-testid="sla-badge" className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 font-semibold">🚨 SLA breached</span>}
                    </td>
                  )}
                  {showCol("phone") && <td className="px-3 py-2">{l.phone}</td>}
                  {showCol("department") && (
                    <td className="px-3 py-2 text-xs" onClick={e => e.stopPropagation()}>
                      {gridMode ? (
                        <Select value={l.department || ""} onValueChange={v => patchLead(l.id, { department: v })}>
                          <SelectTrigger data-testid={`inline-dept-${l.lead_id}`} className="h-7 text-xs px-2"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="STUDY">Study</SelectItem><SelectItem value="FILING">Filing</SelectItem></SelectContent>
                        </Select>
                      ) : (
                        <span className={`stage-badge ${l.department === "FILING" ? "bg-indigo-50 text-indigo-800 border-indigo-200" : "bg-teal-50 text-teal-800 border-teal-200"}`}>{l.department === "FILING" ? "Filing" : "Study"}</span>
                      )}
                    </td>
                  )}
                  {showCol("source") && (
                    <td className="px-3 py-2 text-xs" onClick={e => e.stopPropagation()}>
                      {gridMode ? (
                        <Select value={l.source || ""} onValueChange={v => patchLead(l.id, { source: v })}>
                          <SelectTrigger className="h-7 text-xs px-2"><SelectValue /></SelectTrigger>
                          <SelectContent>{(meta.sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : l.source}
                    </td>
                  )}
                  {showCol("app_type") && <td className="px-3 py-2 text-xs">{l.app_type_name || "—"}</td>}
                  {showCol("stage") && (
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      {gridMode ? (
                        <Select value={l.stage || ""} onValueChange={v => patchLead(l.id, { stage: v })}>
                          <SelectTrigger data-testid={`inline-stage-${l.lead_id}`} className={`h-7 text-xs px-2 ${stageColor(l.stage)}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{(meta.stages || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className={`stage-badge ${stageColor(l.stage)}`}>{l.stage}</span>}
                    </td>
                  )}
                  {showCol("caller") && (
                    <td className="px-3 py-2 text-slate-600" onClick={e => e.stopPropagation()}>
                      {gridMode && canBulkAssign ? (
                        <Select value={l.assigned_caller_id || ""} onValueChange={v => patchLead(l.id, { assigned_caller_id: v || null })}>
                          <SelectTrigger className="h-7 text-xs px-2"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>{meta.callers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (l.caller_name || <span className="text-rose-600">Unassigned</span>)}
                    </td>
                  )}
                  {showCol("last_contact") && <td className="px-3 py-2 text-xs text-slate-500">{fmt(l.last_contacted_at)}</td>}
                  {showCol("next_followup") && (
                    <td className="px-3 py-2 text-xs" onClick={e => e.stopPropagation()}>
                      {gridMode ? (
                        <Input type="date" className="h-7 text-xs" value={l.next_followup_at ? l.next_followup_at.slice(0, 10) : ""} onChange={e => patchLead(l.id, { next_followup_at: e.target.value || null })} />
                      ) : fmt(l.next_followup_at)}
                    </td>
                  )}
                  {showCol("notes") && (
                    <td className="px-3 py-2 text-xs max-w-[200px]" onClick={e => e.stopPropagation()}>
                      {gridMode ? (
                        <Input className="h-7 text-xs" defaultValue={l.notes || ""} onBlur={e => e.target.value !== (l.notes || "") && patchLead(l.id, { notes: e.target.value })} placeholder="—" />
                      ) : (<span className="line-clamp-1 text-slate-600">{l.notes || "—"}</span>)}
                    </td>
                  )}
                  {showCol("age") && <td className="px-3 py-2 text-right text-xs">{l.age_days ?? "—"}d</td>}
                  {showCol("actions") && (
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <a title="Call" href={`tel:${l.phone}`} className="inline-flex mr-1 text-slate-500 hover:text-slate-900"><Phone className="w-4 h-4" /></a>
                      <a title="WhatsApp" href={`https://wa.me/${(l.phone || "").replace(/[^0-9+]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex mr-1 text-slate-500 hover:text-slate-900"><MessageCircle className="w-4 h-4" /></a>
                      {l.email && <a title="Email" href={`mailto:${l.email}`} className="inline-flex mr-1 text-slate-500 hover:text-slate-900"><Mail className="w-4 h-4" /></a>}
                      <button onClick={() => nav(`/leads/${l.id}`)} className="text-xs text-orange-600 hover:underline">Open</button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={20} className="text-center py-6 text-slate-500">No leads match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {sort.col && (
        <div className="text-xs text-slate-500 -mt-2 flex items-center gap-2">
          Sorted by <span className="font-semibold text-slate-700 capitalize">{sort.col.replace(/_/g, " ")}</span> {sort.dir === "asc" ? "↑" : "↓"}
          <button onClick={() => setSort({ col: null, dir: "asc" })} className="text-orange-600 hover:underline">Clear</button>
        </div>
      )}

      {/* Bulk actions bottom bar */}
      {gridMode && selected.size > 0 && (
        <div data-testid="bulk-toolbar" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-slate-900 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 flex-wrap max-w-[95vw]">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <span className="opacity-30">|</span>
          {canBulkAssign && (
            <div className="flex items-center gap-1">
              <span className="text-xs opacity-70">Assign:</span>
              <Select onValueChange={v => bulk("assign_caller", v)}>
                <SelectTrigger data-testid="bulk-assign" className="h-8 min-w-[160px] bg-slate-800 border-slate-700 text-white text-xs"><SelectValue placeholder="Caller…" /></SelectTrigger>
                <SelectContent>{meta.callers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="text-xs opacity-70">Stage:</span>
            <Select onValueChange={v => bulk("change_stage", v)}>
              <SelectTrigger data-testid="bulk-stage" className="h-8 min-w-[140px] bg-slate-800 border-slate-700 text-white text-xs"><SelectValue placeholder="Stage…" /></SelectTrigger>
              <SelectContent>{(meta.stages || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs opacity-70">Team:</span>
            <Select onValueChange={v => bulk("change_department", v)}>
              <SelectTrigger data-testid="bulk-dept" className="h-8 min-w-[110px] bg-slate-800 border-slate-700 text-white text-xs"><SelectValue placeholder="Team…" /></SelectTrigger>
              <SelectContent><SelectItem value="STUDY">Study</SelectItem><SelectItem value="FILING">Filing</SelectItem></SelectContent>
            </Select>
          </div>
          <button onClick={clearSel} data-testid="bulk-clear" className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1"><X className="w-3 h-3" />Clear</button>
        </div>
      )}

      <LeadsImportDialog open={importOpen} onOpenChange={setImportOpen} meta={meta} onImported={load} />

      {/* Save View dialog */}
      <SaveViewDialog open={saveViewOpen} onOpenChange={setSaveViewOpen} onSave={saveNewView} shareScopes={shareScopes} canShare={canShareViews} />

      {/* Rename View dialog */}
      <Dialog open={!!renameView} onOpenChange={(v) => { if (!v) setRenameView(null); }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Rename view</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">New name</Label>
            <Input data-testid="view-rename-input" value={renameView?.name || ""} onChange={e => setRenameView(r => ({ ...r, name: e.target.value }))} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameView(null)}>Cancel</Button>
            <Button data-testid="view-rename-save" onClick={renameSubmit} className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaveViewDialog({ open, onOpenChange, onSave, shareScopes, canShare }) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState("");
  useEffect(() => { if (open) { setName(""); setShared(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>Save current view</DialogTitle>
          <DialogDescription>Save the current filter + column + sort combo. Applies with one click later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">View name</Label>
            <Input data-testid="save-view-name" value={name} onChange={e => setName(e.target.value)} placeholder='e.g. "My Study Leads Today", "Overdue Follow-ups"' autoFocus onKeyDown={e => e.key === "Enter" && name.trim() && onSave(name.trim(), shared || null)} />
          </div>
          {canShare && (
            <div>
              <Label className="text-xs">Sharing</Label>
              <select data-testid="save-view-share" value={shared} onChange={e => setShared(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-3 py-2">
                <option value="">Private (only me)</option>
                {shareScopes.map(s => <option key={s} value={s}>Share with {s === "ALL" ? "everyone" : `${s} team`}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">Shared views appear under "Team Views" for everyone in the chosen scope.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-view-submit" onClick={() => onSave(name.trim(), shared || null)} disabled={!name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">Save view</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
